import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { cx, ui, text, validationStyles } from '../../lib/ui-primitives'

// ============================================
// INPUT COMPONENT
// Unified input with validation states
// ============================================

export type InputVariant = 'default' | 'search'
export type ValidationState = 'none' | 'valid' | 'invalid'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text */
  label?: string
  /** Helper text below input */
  helperText?: string
  /** Error message (overrides helperText) */
  error?: string
  /** Validation state indicator */
  validationState?: ValidationState
  /** Left addon (icon or text) */
  leftAddon?: React.ReactNode
  /** Right addon (icon or text) */
  rightAddon?: React.ReactNode
  /** Full width */
  fullWidth?: boolean
}

/**
 * Input - Text input with validation states
 *
 * Extracted from: DetailsScreen, AmountScreen, LookupScreen
 *
 * Features:
 * - Label with required indicator
 * - Validation dot indicator
 * - Error/helper text
 * - Icon addons
 * - 48px min touch target
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      validationState = 'none',
      leftAddon,
      rightAddon,
      fullWidth = true,
      className,
      id,
      required,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`
    const showValidation = validationState !== 'none' || error

    return (
      <div className={cx(fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className={cx('text-sm mb-2 block', text.secondary)}
          >
            {label}
            {required && ' *'}
          </label>
        )}

        <div className="relative">
          {leftAddon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
              {leftAddon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            required={required}
            className={cx(
              ui.input,
              !!leftAddon && 'pl-10',
              !!rightAddon && 'pr-10',
              !!error && 'border-red-500/50 focus:border-red-500',
              className
            )}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />

          {rightAddon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40">
              {rightAddon}
            </div>
          )}
        </div>

        {/* Validation/Helper Text */}
        {(showValidation || helperText || error) && (
          <div className="flex items-center gap-2 mt-1">
            {showValidation && (
              <span
                className={cx(
                  'size-2 rounded-full shrink-0',
                  error
                    ? validationStyles.invalid
                    : validationState === 'valid'
                      ? validationStyles.valid
                      : validationState === 'invalid'
                        ? validationStyles.invalid
                        : validationStyles.empty
                )}
                aria-hidden="true"
              />
            )}
            <span
              id={error ? `${inputId}-error` : `${inputId}-helper`}
              className={cx(
                'text-xs',
                error ? 'text-red-300' : text.tertiary
              )}
              role={error ? 'alert' : undefined}
            >
              {error || helperText}
            </span>
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

// ============================================
// TEXTAREA COMPONENT
// Multi-line input with same styling
// ============================================

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text */
  label?: string
  /** Helper text below input */
  helperText?: string
  /** Error message */
  error?: string
  /** Validation state indicator */
  validationState?: ValidationState
  /** Full width */
  fullWidth?: boolean
}

/**
 * Textarea - Multi-line text input
 *
 * Extracted from: DetailsScreen (wallet address input)
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      validationState = 'none',
      fullWidth = true,
      className,
      id,
      required,
      ...props
    },
    ref
  ) => {
    const inputId = id || `textarea-${label?.toLowerCase().replace(/\s+/g, '-')}`
    const showValidation = validationState !== 'none' || error

    return (
      <div className={cx(fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className={cx('text-sm mb-2 block', text.secondary)}
          >
            {label}
            {required && ' *'}
          </label>
        )}

        <textarea
          ref={ref}
          id={inputId}
          required={required}
          className={cx(
            ui.textarea,
            error && 'border-red-500/50 focus:border-red-500',
            className
          )}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />

        {/* Validation/Helper Text */}
        {(showValidation || helperText || error) && (
          <div className="flex items-center gap-2 mt-1">
            {showValidation && (
              <span
                className={cx(
                  'size-2 rounded-full shrink-0',
                  error
                    ? validationStyles.invalid
                    : validationState === 'valid'
                      ? validationStyles.valid
                      : validationState === 'invalid'
                        ? validationStyles.invalid
                        : validationStyles.empty
                )}
                aria-hidden="true"
              />
            )}
            <span
              id={error ? `${inputId}-error` : `${inputId}-helper`}
              className={cx(
                'text-xs',
                error ? 'text-red-300' : text.tertiary
              )}
              role={error ? 'alert' : undefined}
            >
              {error || helperText}
            </span>
          </div>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
